/**
 * The user's own data (GDPR Art. 15/20 export, Art. 17 deletion). The export reads everything the
 * API rules let the user see (their account, workspaces and what is in them, document history,
 * conversations, subscription, linked sign-ins) and zips it in the browser with the uploaded
 * files. Deletion confirms the password, ends the subscription (services/billing) and lets the
 * server delete the account (server/pb_hooks/account.pb.js).
 */
import type { RecordModel } from "pocketbase";
import { cancelSubscription } from "./billing";
import { currentWorkspaceId } from "./workspaces";
import { pb } from "@/lib/core/pocketbase";

/** Collections exported as <file>.json, with what the user can read of them. */
const COLLECTIONS = [
  ["workspaces", "workspaces"],
  ["customers", "customers"],
  ["invoices", "invoices-quotes-templates"],
  ["recurring_invoices", "recurring-invoices"],
  ["payment_methods", "payment-methods"],
  ["invitations", "invitations"],
  ["audit_log", "document-history"],
  ["conversations", "assistant-conversations"],
  ["connected_apps", "connected-apps"],
  ["subscriptions", "subscription"],
  ["_externalAuths", "linked-sign-ins"],
  ["_authOrigins", "sign-in-devices"],
] as const;

/** File fields exported to files/; the stored documents are protected (file token). */
const FILE_FIELDS: Record<string, string[]> = {
  users: ["avatar"],
  workspaces: ["companyLogo"],
  invoices: ["companyLogo", "document", "einvoice"],
};

export type AccountExport = { files: Record<string, Uint8Array>; name: string };

/** Everything as JSON files plus the uploaded and stored files (files/<record>-<name>). */
export async function collectAccountData(now = new Date()): Promise<AccountExport> {
  const encoder = new TextEncoder();
  const json = (value: unknown) => encoder.encode(JSON.stringify(value, null, 2));
  const userId = pb.authStore.record?.id ?? "";
  const user = await pb.collection("users").getOne(userId);
  const files: Record<string, Uint8Array> = { "account.json": json(user) };
  const records: Record<string, RecordModel[]> = { users: [user as RecordModel] };

  for (const [collection, file] of COLLECTIONS) {
    records[collection] = await pb.collection(collection).getFullList();
    files[`${file}.json`] = json(records[collection]);
  }
  // A file token lasts a few minutes; a long export asks for a new one.
  let token = "";
  let tokenAt = 0;
  for (const [collection, fields] of Object.entries(FILE_FIELDS)) {
    for (const record of records[collection] ?? []) {
      for (const field of fields) {
        const name = record[field] as string | undefined;
        if (!name) continue;
        if (Date.now() - tokenAt > 60_000) {
          token = await pb.files.getToken();
          tokenAt = Date.now();
        }
        const res = await fetch(pb.files.getURL(record, name, { token }));
        if (res.ok) files[`files/${record.id}-${name}`] = new Uint8Array(await res.arrayBuffer());
      }
    }
  }
  files["README.txt"] = encoder.encode(
    "Your Blanqo data: one JSON file per kind of record. files/ holds the uploaded images and the\n" +
      "stored PDF and e-invoice XML of every finalised invoice (<record id>-<file name>).\n" +
      "Quotes, drafts and templates have no stored PDF: download those you need from the app.\n",
  );
  return { files, name: `blanqo-export-${now.toISOString().slice(0, 10)}.zip` };
}

/** Builds the ZIP (fflate loads only when someone exports). */
export async function exportAccountData(): Promise<{ blob: Blob; filename: string }> {
  const [{ zipSync }, data] = await Promise.all([import("fflate"), collectAccountData()]);
  const zip = zipSync(data.files, { level: 6 });
  return { blob: new Blob([zip], { type: "application/zip" }), filename: data.name };
}

/** The step of a deletion that failed: nothing changed before "subscription" succeeded. */
export type DeletionStep = "password" | "subscription" | "delete";

export class AccountDeletionError extends Error {
  constructor(
    readonly step: DeletionStep,
    cause: unknown,
  ) {
    super(cause instanceof Error ? cause.message : String(cause), { cause });
  }
}

async function step<T>(name: DeletionStep, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    throw new AccountDeletionError(name, error);
  }
}

/**
 * Deletes the account for good: the password is confirmed first (a wrong one changes nothing),
 * then the subscription ends and the server deletes the account and owned workspaces. The
 * subscription has to end first: the billing service only acts for a signed-in user, a deleted
 * account can no longer sign in, and the server refuses while one runs. Both steps are safe to
 * repeat after a failure. `retention` confirms the user keeps their own copies of finalised
 * invoices; the server refuses without it while owned workspaces hold any (the dialog asks first,
 * so the subscription is never ended for nothing).
 */
export async function deleteAccount(password: string, retention = false): Promise<void> {
  const email = pb.authStore.record?.email as string;
  await step("password", () => pb.collection("users").authWithPassword(email, password));
  await step("subscription", cancelSubscription);
  await step("delete", () =>
    pb.send("/api/blanqo/account/delete", { method: "POST", body: { password, retention } }),
  );
  pb.authStore.clear();
}

/**
 * How many finalised invoices a deletion would take with it: in the current workspace, or in every
 * workspace the user owns (account deletion). The server refuses either without `retention`.
 */
export async function countFinalisedInvoices(scope: "workspace" | "owned"): Promise<number> {
  const filter =
    scope === "workspace"
      ? pb.filter('workspace = {:workspace} && finalizedAt != ""', {
          workspace: currentWorkspaceId(),
        })
      : pb.filter('workspace.owner = {:user} && finalizedAt != ""', {
          user: pb.authStore.record?.id ?? "",
        });
  const page = await pb.collection("invoices").getList(1, 1, { filter, fields: "id" });
  return page.totalItems;
}
